// utils/reportUtils.js

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const generatePOSummaryReport = (northData, southData, kanbanData, northStats, southStats, kanbanStats) => {
  return new Promise((resolve, reject) => {
    try {
      // Generate the PDF
      const doc = new jsPDF({ orientation: 'landscape' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const primaryColor = '#384f77';
      const white = '#ffffff';
      const currentDate = new Date();

      // Header
      doc.setFillColor(white);
      doc.rect(0, 0, pageWidth, 20, 'F');
      doc.setFontSize(24);
      doc.setTextColor(primaryColor);
      doc.text('PO SUMMARY REPORT', pageWidth / 2, 14, { align: 'center' });

      // Company Information
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setTextColor(primaryColor);
      doc.setFont('helvetica', 'bold');
      doc.text('Advanced Controls & Automation', 15, 30);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('574 Hudson River Road', 15, 36);
      doc.text('Waterford', 15, 42);
      doc.text('NY 12188', 15, 48);

      // Report Information
      doc.setFontSize(10);
      doc.setTextColor(primaryColor);
      doc.text(`Generated on: ${currentDate.toLocaleDateString()}`, 15, 60);
      
      let currentY = 70;

      // North Shop Table
      if (northData.length > 0) {
        // North Shop Summary
        doc.setFontSize(14);
        doc.setTextColor(primaryColor);
        doc.text('North Shop', 15, currentY);
        currentY += 10;

        // North Shop Stats
        doc.setFontSize(10);
        doc.text(`Individual POs: ${northStats.totalPOs}`, 15, currentY);
        doc.text(`Total $ Balance: $${northStats.totalBalance.toLocaleString()}`, 120, currentY);
        doc.text(`Overdue Line Items: ${northStats.overdueJobs}`, 225, currentY);
        currentY += 10;

        // Format data for the table
        const formattedNorthData = northData.map(row => ({
          PO: row.PO,
          'Line': row['PO Line #'],
          'PART#': row['PART#'],
          'DESCRIPTION': row.DESCRIPTION.length > 15 ? row.DESCRIPTION.slice(0, 15) + '...' : row.DESCRIPTION,
          'DATE DUE': row['DATE DUE'],
          'Order QTY': row['QTY on Order'],
          'SHIPPED': row['TOTAL QTY SHIPPED'],
          '% SHIPPED': `${Math.round((row['TOTAL QTY SHIPPED'] / row['QTY on Order']) * 100) || 0}%`,
          'BALANCE': row['ORDER QTY BALANCE'],
          '$ BALANCE': row['$ BALANCE']
        }));

        // North Shop Table Headers
        const northHeaders = Object.keys(formattedNorthData[0]);
        
        // Generate North Shop table
        autoTable(doc, {
          startY: currentY,
          head: [northHeaders],
          body: formattedNorthData.map(row => Object.values(row)),
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: primaryColor, textColor: white, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [240, 240, 240] },
          margin: { left: 15, right: 15 },
          didParseCell: (data) => {
            // Highlight overdue rows
            if (data.section === 'body') {
              const row = formattedNorthData[data.row.index];
              if (row && row['DATE DUE'] !== 'KANBAN' && new Date(row['DATE DUE']) < currentDate) {
                data.cell.styles.fillColor = [255, 255, 0]; // Yellow highlight
              }
            }
          },
        });

        currentY = doc.lastAutoTable.finalY + 15;
      }

      // South Shop Table
      if (southData.length > 0) {
        // Check if we need a new page
        if (currentY > pageHeight - 100) {
          doc.addPage();
          currentY = 20;
        }

        // South Shop Summary
        doc.setFontSize(14);
        doc.setTextColor(primaryColor);
        doc.text('South Shop', 15, currentY);
        currentY += 10;

        // South Shop Stats
        doc.setFontSize(10);
        doc.text(`Individual POs: ${southStats.totalPOs}`, 15, currentY);
        doc.text(`Total $ Balance: $${southStats.totalBalance.toLocaleString()}`, 120, currentY);
        doc.text(`Overdue Line Items: ${southStats.overdueJobs}`, 225, currentY);
        currentY += 10;

        // Format data for the table
        const formattedSouthData = southData.map(row => ({
          PO: row.PO,
          'Line': row['PO Line #'],
          'PART#': row['PART#'],
          'DESCRIPTION': row.DESCRIPTION.length > 15 ? row.DESCRIPTION.slice(0, 15) + '...' : row.DESCRIPTION,
          'DATE DUE': row['DATE DUE'],
          'Order QTY': row['QTY on Order'],
          'SHIPPED': row['TOTAL QTY SHIPPED'],
          '% SHIPPED': `${Math.round((row['TOTAL QTY SHIPPED'] / row['QTY on Order']) * 100) || 0}%`,
          'BALANCE': row['ORDER QTY BALANCE'],
          '$ BALANCE': row['$ BALANCE']
        }));

        // South Shop Table Headers
        const southHeaders = Object.keys(formattedSouthData[0]);
        
        // Generate South Shop table
        autoTable(doc, {
          startY: currentY,
          head: [southHeaders],
          body: formattedSouthData.map(row => Object.values(row)),
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: primaryColor, textColor: white, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [240, 240, 240] },
          margin: { left: 15, right: 15 },
          didParseCell: (data) => {
            // Highlight overdue rows
            if (data.section === 'body') {
              const row = formattedSouthData[data.row.index];
              if (row && row['DATE DUE'] !== 'KANBAN' && new Date(row['DATE DUE']) < currentDate) {
                data.cell.styles.fillColor = [255, 255, 0]; // Yellow highlight
              }
            }
          },
        });

        currentY = doc.lastAutoTable.finalY + 15;
      }

      // Kanban Jobs Table
      if (kanbanData.length > 0) {
        // Check if we need a new page
        if (currentY > pageHeight - 100) {
          doc.addPage();
          currentY = 20;
        }

        // Kanban Jobs Summary
        doc.setFontSize(14);
        doc.setTextColor(primaryColor);
        doc.text('Kanban Jobs', 15, currentY);
        currentY += 10;

        // Kanban Jobs Stats
        doc.setFontSize(10);
        doc.text(`Individual POs: ${kanbanStats.totalPOs}`, 15, currentY);
        doc.text(`Total $ Balance: $${kanbanStats.totalBalance.toLocaleString()}`, 120, currentY);
        currentY += 10;

        // Format data for the table
        const formattedKanbanData = kanbanData.map(row => ({
          PO: row.PO,
          'Line': row['PO Line #'],
          'PART#': row['PART#'],
          'DESCRIPTION': row.DESCRIPTION.length > 15 ? row.DESCRIPTION.slice(0, 15) + '...' : row.DESCRIPTION,
          'Order QTY': row['QTY on Order'],
          'SHIPPED': row['TOTAL QTY SHIPPED'],
          '% SHIPPED': `${Math.round((row['TOTAL QTY SHIPPED'] / row['QTY on Order']) * 100) || 0}%`,
          'BALANCE': row['ORDER QTY BALANCE'],
          '$ BALANCE': row['$ BALANCE']
        }));

        // Kanban Jobs Table Headers
        const kanbanHeaders = Object.keys(formattedKanbanData[0]);
        
        // Generate Kanban Jobs table
        autoTable(doc, {
          startY: currentY,
          head: [kanbanHeaders],
          body: formattedKanbanData.map(row => Object.values(row)),
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: primaryColor, textColor: white, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [240, 240, 240] },
          margin: { left: 15, right: 15 }
        });
      }

      // Save the PDF
      doc.save('po_summary_report.pdf');
      resolve(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      reject(error);
    }
  });
};